/**
 * form-drawer.js
 * Generic bottom-sheet (mobile) and side-drawer (desktop) controller.
 *
 * HTML contract:
 *   [data-drawer-trigger="<id>"]        — any element that opens the drawer
 *   #<id>.form-drawer                   — the drawer panel
 *   #<id>-backdrop.form-drawer-backdrop — the backdrop overlay
 *   .form-drawer-close (inside drawer)  — button that closes the drawer
 *
 * Programmatic API:
 *   window.FormDrawerController.open('<id>')
 *   window.FormDrawerController.close('<id>')
 */
(function () {
  'use strict';

  if (typeof window.FormDrawerController !== 'undefined') return;

  var savedScrollY = 0;
  var openCount = 0;

  function lockBodyScroll() {
    if (openCount === 0) {
      savedScrollY = window.scrollY || window.pageYOffset;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + savedScrollY + 'px';
      document.body.style.width = '100%';
    }
    openCount++;
  }

  function unlockBodyScroll() {
    if (openCount > 0) openCount--;
    if (openCount === 0) {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, savedScrollY);
    }
  }

  // Returns visible, reachable focusable elements inside a drawer panel.
  // Excludes visibility:hidden elements; they retain offsetWidth/Height but
  // the browser skips them in the Tab order, so they must not appear as first
  // or last in the wrap calculation.
  function formDrawerFocusable(drawer) {
    var sel = 'a[href],button:not([disabled]),input:not([disabled]),' +
              'select:not([disabled]),textarea:not([disabled]),' +
              'summary,[tabindex]:not([tabindex="-1"])';
    return Array.from(drawer.querySelectorAll(sel)).filter(function(el) {
      if (el.offsetWidth === 0 && el.offsetHeight === 0 && el !== document.activeElement) return false;
      var s = window.getComputedStyle(el);
      return s.visibility !== 'hidden' && s.display !== 'none';
    });
  }

  function openDrawer(drawerId) {
    var drawer = document.getElementById(drawerId);
    var backdrop = document.getElementById(drawerId + '-backdrop');
    if (!drawer) return;
    // Store the opener so focus can return to it on close.
    drawer._savedFocus = document.activeElement;
    // Teleport to body so the fixed drawer escapes any section stacking context.
    // Yuva's lazy-load CSS (filter/will-change on img.lazyload) propagates stacking
    // contexts up the tree, trapping position:fixed children below the header.
    // Yuva's own drawers (.yv_side_drawer_wrapper) live at body level — we match that.
    if (drawer.parentElement !== document.body) {
      drawer._origParent = drawer.parentElement;
      drawer._origNext = drawer.nextSibling;
      document.body.appendChild(drawer);
    }
    if (backdrop && backdrop.parentElement !== document.body) {
      backdrop._origParent = backdrop.parentElement;
      backdrop._origNext = backdrop.nextSibling;
      document.body.appendChild(backdrop);
    }
    drawer.classList.add('is-open');
    if (backdrop) backdrop.classList.add('is-open');
    lockBodyScroll();
    // Remove inert so the drawer and its contents are reachable.
    drawer.removeAttribute('inert');
    // Move focus to the first focusable element inside the drawer; fall back
    // to the panel itself (tabindex="-1") so keyboard users are placed inside.
    var f = formDrawerFocusable(drawer);
    if (f.length) {
      f[0].focus({ preventScroll: true });
    } else {
      drawer.focus();
    }
  }

  function closeDrawer(drawerId) {
    var drawer = document.getElementById(drawerId);
    var backdrop = document.getElementById(drawerId + '-backdrop');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    if (backdrop) backdrop.classList.remove('is-open');
    unlockBodyScroll();
    // Inert the closed drawer before moving it back so Tab cannot reach it.
    drawer.setAttribute('inert', '');
    // Return focus to the element that opened the drawer.
    if (drawer._savedFocus && typeof drawer._savedFocus.focus === 'function') {
      drawer._savedFocus.focus();
      delete drawer._savedFocus;
    }
    // Return drawer and backdrop to their original DOM positions
    if (drawer._origParent) {
      drawer._origParent.insertBefore(drawer, drawer._origNext || null);
      delete drawer._origParent;
      delete drawer._origNext;
    }
    if (backdrop && backdrop._origParent) {
      backdrop._origParent.insertBefore(backdrop, backdrop._origNext || null);
      delete backdrop._origParent;
      delete backdrop._origNext;
    }
  }

  function teleportToBody() {
    // Only sticky bars need to escape section stacking contexts — they are
    // always visible on mobile and get buried under other sections without
    // this. Drawers and backdrops must stay inside their parent <form> element
    // so hidden inputs and the ATC button remain in the same form context.
    document.querySelectorAll('.pf-sticky-bar').forEach(function (el) {
      if (el.parentElement !== document.body) {
        document.body.appendChild(el);
      }
    });
  }

  function init() {
    teleportToBody();

    // Seed inert on all closed drawers so their contents are off the Tab
    // order and out of the accessibility tree until opened.
    document.querySelectorAll('.form-drawer:not(.is-open)').forEach(function (drawer) {
      drawer.setAttribute('inert', '');
    });

    document.querySelectorAll('[data-drawer-trigger]').forEach(function (trigger) {
      var drawerId = trigger.getAttribute('data-drawer-trigger');
      trigger.addEventListener('click', function () { openDrawer(drawerId); });
    });

    document.querySelectorAll('.form-drawer').forEach(function (drawer) {
      var drawerId = drawer.id;
      var closeBtn = drawer.querySelector('.form-drawer-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', function () { closeDrawer(drawerId); });
      }
    });

    document.querySelectorAll('.form-drawer-backdrop').forEach(function (backdrop) {
      var drawerId = backdrop.id.replace(/-backdrop$/, '');
      backdrop.addEventListener('click', function () { closeDrawer(drawerId); });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      document.querySelectorAll('.form-drawer.is-open').forEach(function (openEl) {
        closeDrawer(openEl.id);
      });
    });

    // Tab trap: document-level capture fires before any child handler can stop
    // propagation, ensuring Tab never escapes an open form-drawer.
    document.addEventListener('keydown', function(e) {
      if (e.key !== 'Tab') return;
      var openDrawerEl = document.querySelector('.form-drawer.is-open');
      if (!openDrawerEl) return;
      var f = formDrawerFocusable(openDrawerEl);
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === openDrawerEl)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }, true);
  }

  window.FormDrawerController = { open: openDrawer, close: closeDrawer };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
